import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { GenerationProjectSummaryDto } from '@mpr/shared';

import { api, type GenerationJob } from '../lib/api.js';
import { splitTextOutputs, stepLabelKey, pickPromptJob } from '../lib/studioGoal.js';
import {
  groupOutputs,
  mediaOfJob,
  mediaOfPlan,
  pickDefaultMediaKeys,
  pickDefaultTextJob,
  projectBadges,
  projectIsMoving,
  toggleMediaKey,
  isFinished,
  type OutputChain,
  type PublishableMedia,
  type StudioHandoff,
} from '../lib/studioProject.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';
import { PublishMenu } from './PublishMenu.js';
import { ResultCard } from './ResultCard.js';
import { ProduceSection } from './StudioProduce.js';
import { CostLine } from './StudioCost.js';
import { VideoPlanEditor } from './VideoPlanPanel.js';

/**
 * The studio's archive: one card per request, however many files it made.
 *
 * It used to list every generated record on its own, so one idea showed up as
 * a text card, an image card and a video card with nothing to say they belonged
 * together. A project is the thing the person asked for; opening it shows all
 * of what came back and lets them choose what goes out.
 */

const PAGE_SIZE = 20;

/* -------------------------------------------------------------------------- */
/*  The list                                                                   */
/* -------------------------------------------------------------------------- */

export function ProjectArchive({
  onSendToPublish,
  onOpenScheduled,
}: {
  onSendToPublish: (handoff: StudioHandoff) => void;
  onOpenScheduled?: () => void;
}) {
  const { t } = useI18n();
  const [openId, setOpenId] = useState<string | null>(null);

  const projects = useInfiniteQuery({
    queryKey: ['generation-projects'],
    queryFn: ({ pageParam }) => api.listGenerationProjects({ limit: PAGE_SIZE, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    // A project still generating keeps its card honest without a reload.
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) => page.projects.some((project) => project.inProgress)) ? 5_000 : false,
  });

  if (openId) {
    return (
      <ProjectDetail
        projectId={openId}
        onBack={() => setOpenId(null)}
        onSendToPublish={onSendToPublish}
        onOpenScheduled={onOpenScheduled}
      />
    );
  }

  if (projects.isLoading) return <div className="loading">{t('common.loading')}</div>;
  if (projects.isError) return <ErrorNotice error={projects.error} />;

  const items = projects.data?.pages.flatMap((page) => page.projects) ?? [];

  return (
    <>
      {items.length === 0 && (
        <div className="empty-state">
          <Icon.sparkle />
          <p>{t('studio.libraryEmpty')}</p>
        </div>
      )}

      <div className="project-grid">
        {items.map((project) => (
          <ProjectCard key={project.id} project={project} onOpen={() => setOpenId(project.id)} />
        ))}
      </div>

      {projects.hasNextPage && (
        <div className="actions">
          <button
            type="button"
            className="ghost"
            onClick={() => void projects.fetchNextPage()}
            disabled={projects.isFetchingNextPage}
          >
            {projects.isFetchingNextPage ? t('common.loading') : t('studio.project.loadMore')}
          </button>
        </div>
      )}
    </>
  );
}

function ProjectCard({ project, onOpen }: { project: GenerationProjectSummaryDto; onOpen: () => void }) {
  const { t } = useI18n();
  const fmt = useFormatters();
  const badges = projectBadges(project);

  return (
    <article className="card project-card">
      <ProjectThumb mediaId={project.thumbnailMediaId} />
      <div className="project-card-body">
        <strong className="project-card-title">{project.title}</strong>
        <span className="muted tiny">{fmt.dateTime(project.createdAt)}</span>
        {badges.counts.length > 0 && <CostLine cost={project} />}
        <div className="project-badges">
          {badges.counts.map(({ kind, count }) => (
            <span key={kind} className="badge">
              {t(`studio.project.count.${kind}` as TranslationKey, { count })}
            </span>
          ))}
          {badges.inProgress && (
            <span className="badge accent">
              <Icon.refresh className="spin" /> {t('studio.project.inProgress')}
            </span>
          )}
          {badges.published && <span className="badge ok">{t('studio.project.published')}</span>}
        </div>
        <div className="actions" style={{ marginTop: 6 }}>
          <button type="button" className="ghost small" onClick={onOpen}>
            {t('studio.project.open')}
          </button>
        </div>
      </div>
    </article>
  );
}

/**
 * A picture of the project. The summary names a media file without saying
 * whether it is an image or a video, so an image is tried first and a failure
 * to decode it falls back to the video's first frame.
 */
function ProjectThumb({ mediaId, small }: { mediaId: string | null; small?: boolean }) {
  const [asVideo, setAsVideo] = useState(false);
  const className = small ? 'project-thumb small' : 'project-thumb';

  if (!mediaId) {
    return (
      <div className={`${className} empty`} aria-hidden="true">
        <Icon.sparkle />
      </div>
    );
  }

  const src = `/api/media/${mediaId}`;
  return (
    <div className={className}>
      {asVideo ? (
        <video src={`${src}#t=0.1`} muted playsInline preload="metadata" />
      ) : (
        <img src={src} alt="" loading="lazy" onError={() => setAsVideo(true)} />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  One project                                                                */
/* -------------------------------------------------------------------------- */

export function ProjectDetail({
  projectId,
  onBack,
  backLabel = 'studio.project.back',
  onSendToPublish,
  onOpenScheduled,
}: {
  projectId: string;
  onBack: () => void;
  /** The back button's wording: "back to the archive", or "new request" when opened from the composer. */
  backLabel?: TranslationKey;
  onSendToPublish: (handoff: StudioHandoff) => void;
  onOpenScheduled?: () => void;
}) {
  const { t } = useI18n();
  const fmt = useFormatters();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['generation-project', projectId],
    queryFn: () => api.getGenerationProject(projectId),
    // Jobs run in the background; poll only while something is moving.
    refetchInterval: (q) => (projectIsMoving(q.state.data) ? 3_000 : false),
  });
  const project = query.data;

  // What the person ticked. Until they tick anything the defaults apply, so
  // the choice is theirs the moment they make one and never before.
  const [textPick, setTextPick] = useState<string | null>(null); // '' = no text
  const [mediaPick, setMediaPick] = useState<string[] | null>(null);

  const archive = useMutation({
    mutationFn: () => api.archiveGenerationProject(projectId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['generation-projects'] });
      onBack();
    },
  });

  const outputs = useMemo(() => groupOutputs(project?.jobs ?? []), [project?.jobs]);
  const roleRefs = project?.options?.attachments ?? [];

  const media = useMemo(() => {
    if (!project) return [] as PublishableMedia[];
    const fromJobs = [...outputs.image, ...outputs.video].map((chain) => mediaOfJob(chain.latest));
    const fromPlans = project.plans.map(mediaOfPlan);
    return [...fromJobs, ...fromPlans].filter((m): m is PublishableMedia => m !== null);
  }, [project, outputs.image, outputs.video]);

  if (!project) {
    return (
      <>
        <button type="button" className="ghost small" onClick={onBack}>
          {t(backLabel)}
        </button>
        {query.isError ? <ErrorNotice error={query.error} /> : <div className="loading">{t('common.loading')}</div>}
      </>
    );
  }

  // A goal project's text is two things: the post to publish, and the prompt
  // that will drive the picture, voice or video. Only the first can be posted.
  const { post: postChains, prompt: promptChains } = splitTextOutputs(outputs.text);
  const promptJob = pickPromptJob(promptChains);
  const textJobs = postChains.map((chain) => chain.latest);
  const defaultText = pickDefaultTextJob(textJobs);
  const chosenText = textPick === '' ? null : (textJobs.find((job) => job.id === textPick) ?? defaultText);

  const selectedKeys = (mediaPick ?? pickDefaultMediaKeys(media)).filter((key) => media.some((m) => m.key === key));
  const selectedMedia = media.filter((m) => selectedKeys.includes(m.key));
  const message = chosenText?.resultText ?? null;

  const toggle = (key: string) => setMediaPick(toggleMediaKey(selectedKeys, key, media));

  const hasOutputs =
    outputs.text.length + outputs.image.length + outputs.audio.length + outputs.video.length + project.plans.length > 0;
  // A prompt on its own, or a voice on its own, is not something a post can carry.
  const hasPublishable = postChains.length + outputs.image.length + outputs.video.length + project.plans.length > 0;

  const titleFor = (job: GenerationJob, capability: GenerationJob['capability']) => {
    if (capability !== 'SCRIPT') return t(`studio.capability.${capability}` as TranslationKey);
    if (job.stage === 'POST_TEXT') return t('studio.step.POST_TEXT');
    if (job.stage === 'MEDIA_PROMPT') {
      return t(stepLabelKey({ stage: 'MEDIA_PROMPT', capability: 'SCRIPT', purpose: job.purpose }));
    }
    return t('studio.capability.SCRIPT');
  };

  const chainBlock = (chains: OutputChain[], capability: GenerationJob['capability']) =>
    chains.map((chain) => {
      const job = chain.latest;
      const ready = isFinished(job.status);
      const publishable = media.find((m) => m.key === `job:${job.id}`);
      return (
        <div key={job.id}>
          <ResultCard
            title={titleFor(job, capability)}
            job={job}
            projectId={project.id}
            onRevised={() => undefined}
            pick={
              capability === 'SCRIPT' && job.stage !== 'MEDIA_PROMPT' && ready && job.resultText ? (
                <label className="check-row project-pick">
                  <input
                    type="radio"
                    name={`project-text-${project.id}`}
                    checked={chosenText?.id === job.id}
                    onChange={() => setTextPick(job.id)}
                  />
                  <span className="tiny">{t('studio.project.useText')}</span>
                </label>
              ) : publishable ? (
                <label className="check-row project-pick">
                  <input
                    type="checkbox"
                    checked={selectedKeys.includes(publishable.key)}
                    onChange={() => toggle(publishable.key)}
                  />
                  <span className="tiny">{t('studio.project.useMedia')}</span>
                </label>
              ) : capability === 'AUDIO' && ready ? (
                <p className="tiny muted">{t('studio.project.audioNotSent')}</p>
              ) : undefined
            }
          />
          <EarlierVersions versions={chain.earlier} />
        </div>
      );
    });

  return (
    <div className="project-detail">
      <div className="panel-head">
        <button type="button" className="ghost small" onClick={onBack}>
          {t(backLabel)}
        </button>
        <button
          type="button"
          className="danger small"
          disabled={archive.isPending}
          onClick={() => {
            if (window.confirm(t('studio.project.archiveConfirm'))) archive.mutate();
          }}
        >
          <Icon.trash />
          {t('studio.project.archive')}
        </button>
      </div>
      {archive.isError && <ErrorNotice error={archive.error} />}

      <h3 className="project-title">{project.title}</h3>
      <p className="muted small">{fmt.dateTime(project.createdAt)}</p>
      <CostLine cost={project} />

      {/* The request exactly as it was typed, and the files it started from. */}
      <section className="card project-request">
        <strong>{t('studio.project.request')}</strong>
        <p className="text-cell">{project.requestText}</p>
        {project.goal && (
          <p className="small">
            <span className="muted">{t('studio.project.goal')}: </span>
            <span className="badge accent">{t(`studio.goal.${project.goal}` as TranslationKey)}</span>
            {project.followUp !== 'NONE' && (
              <span className="badge">+ {t(`studio.followUp.${project.followUp}` as TranslationKey)}</span>
            )}
          </p>
        )}
        {(project.referenceMediaIds.length > 0 || roleRefs.length > 0) && (
          <>
            <span className="tiny muted">{t('studio.attachments')}</span>
            <div className="project-refs">
              {project.referenceMediaIds
                .filter((id) => !roleRefs.some((ref) => ref.mediaId === id))
                .map((id) => (
                  <ProjectThumb key={id} mediaId={id} small />
                ))}
              {roleRefs.map((ref) => (
                <div key={ref.mediaId ?? ref.url} className="project-ref">
                  {ref.mediaId ? (
                    <ProjectThumb mediaId={ref.mediaId} small />
                  ) : (
                    <a className="tiny ltr" dir="ltr" href={ref.url} target="_blank" rel="noreferrer">
                      {t('studio.att.link')}
                    </a>
                  )}
                  <span className="badge">{t(`studio.role.${ref.role}` as TranslationKey)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Every output of the request in one place. */}
      <section className="project-outputs">
        <h3>{t('studio.project.outputs')}</h3>
        {!hasOutputs && <p className="muted small">{t('studio.project.noOutputs')}</p>}

        {outputs.text.length > 0 && (
          // The post and its media prompt side by side, so one glance shows
          // what will be said and what the picture or video will be told.
          <div className="goal-pair">
            {postChains.length > 0 && (
              <div className="goal-pair-col">
                <h4>{t(promptChains.length > 0 ? 'studio.project.postTextHeading' : 'studio.project.textHeading')}</h4>
                {chainBlock(postChains, 'SCRIPT')}
                {textJobs.some((job) => isFinished(job.status) && job.resultText) && (
                  <label className="check-row project-pick">
                    <input
                      type="radio"
                      name={`project-text-${project.id}`}
                      checked={chosenText === null}
                      onChange={() => setTextPick('')}
                    />
                    <span className="tiny">{t('studio.project.noText')}</span>
                  </label>
                )}
              </div>
            )}
            {promptChains.length > 0 && (
              <div className="goal-pair-col">
                <h4>{t('studio.project.promptHeading')}</h4>
                {chainBlock(promptChains, 'SCRIPT')}
              </div>
            )}
          </div>
        )}

        {/* Once the prompt is finished, the button that turns it into the thing itself. */}
        {promptJob && <ProduceSection project={project} promptJob={promptJob} />}

        {outputs.image.length > 0 && (
          <>
            <h4>{t('studio.project.imageHeading')}</h4>
            {chainBlock(outputs.image, 'IMAGE')}
          </>
        )}

        {outputs.audio.length > 0 && (
          <>
            <h4>{t('studio.project.audioHeading')}</h4>
            {chainBlock(outputs.audio, 'AUDIO')}
          </>
        )}

        {outputs.video.length > 0 && (
          <>
            <h4>{t('studio.project.videoHeading')}</h4>
            {chainBlock(outputs.video, 'VIDEO')}
          </>
        )}

        {project.plans.length > 0 && (
          <>
            <h4>{t('studio.project.plansHeading')}</h4>
            {project.plans.map((plan) => {
              const publishable = media.find((m) => m.key === `plan:${plan.id}`);
              return (
                <div key={plan.id}>
                  {publishable && (
                    <label className="check-row project-pick">
                      <input
                        type="checkbox"
                        checked={selectedKeys.includes(publishable.key)}
                        onChange={() => toggle(publishable.key)}
                      />
                      <span className="tiny">{t('studio.project.useMedia')}</span>
                    </label>
                  )}
                  <VideoPlanEditor planId={plan.id} initialPlan={plan} />
                </div>
              );
            })}
          </>
        )}
      </section>

      {/* What will be sent, spelled out, and the two ways to send it. */}
      {hasPublishable && (
        <section className="card project-publish">
          <strong>{t('studio.project.readyTitle')}</strong>
          <dl className="facts">
            <dt>{t('studio.project.sendText')}</dt>
            <dd>{message ? <span className="text-cell">{message}</span> : <span className="muted">{t('studio.project.noText')}</span>}</dd>
            <dt>{t('studio.project.sendMedia')}</dt>
            <dd>
              {selectedMedia.length === 0 ? (
                <span className="muted">{t('studio.project.noMedia')}</span>
              ) : (
                selectedMedia.map((m) => (
                  <span key={m.key} className="badge">
                    {t(`studio.project.kind.${m.kind}` as TranslationKey)}
                  </span>
                ))
              )}
            </dd>
          </dl>
          <p className="tiny muted">{t('studio.project.postRule')}</p>
          <PublishMenu
            projectId={project.id}
            message={message}
            media={selectedMedia}
            aspect={project.options?.video?.aspect}
            onNewPost={onSendToPublish}
            onOpenScheduled={onOpenScheduled}
          />
        </section>
      )}

      <section className="project-publications">
        <h3>{t('studio.project.publishing')}</h3>
        {project.publications.length === 0 ? (
          <p className="muted small">{t('studio.project.notPublished')}</p>
        ) : (
          <ul className="project-publication-list">
            {project.publications.map((publication) => (
              <li key={publication.id} className="small">
                <span className={`badge ${publication.mode === 'NEW_POST' ? 'ok' : 'accent'}`}>
                  {t(`studio.project.mode.${publication.mode}` as TranslationKey)}
                </span>{' '}
                <span className="muted">
                  {fmt.dateTime(publication.createdAt)} ·{' '}
                  {t('studio.attach.mediaCount', { count: publication.mediaIds.length })}
                </span>{' '}
                {onOpenScheduled && (
                  <button type="button" className="link tiny" onClick={onOpenScheduled}>
                    {t('studio.project.seeInPosts')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** The versions a revision replaced, folded away: the history, not the result. */
function EarlierVersions({ versions }: { versions: GenerationJob[] }) {
  const { t } = useI18n();
  const fmt = useFormatters();
  if (versions.length === 0) return null;

  return (
    <details className="project-earlier">
      <summary className="tiny muted">{t('studio.project.earlierVersions', { count: versions.length })}</summary>
      <ol className="studio-revision-list">
        {versions.map((version) => (
          <li key={version.id} className="tiny muted">
            {version.feedbackText
              ? t('studio.revisionEntry', { feedback: version.feedbackText, date: fmt.dateTime(version.createdAt) })
              : fmt.dateTime(version.createdAt)}
            {version.resultText ? ` — ${version.resultText.slice(0, 120)}` : ''}
          </li>
        ))}
      </ol>
    </details>
  );
}
